/**
 * Textos de interface, em português (RNF-U04).
 *
 * Regra do RNF-U03: nenhuma mensagem técnica chega ao usuário final, e toda
 * falha indica a ação corretiva. Se um texto novo não disser ao voluntário
 * *o que fazer agora*, ele ainda não está pronto.
 */

export const messages = {
  auth: {
    /**
     * ── A ÚNICA TELA QUE ALGUÉM VÊ SEM ESTAR LOGADO ─────────────────────────
     *
     * Quem chega aqui é voluntário da liderança (PRD §2.1), recebeu o endereço
     * por WhatsApp e não abre o painel toda semana. A copy responde, nessa
     * ordem, às três perguntas que ele traz: **de quem é isto**, **por que eu
     * teria acesso** e **o que faço agora**.
     *
     * Duas coisas que esta copy NÃO pode fazer, e por quê:
     *
     * 1. **Não convida a se cadastrar.** Acesso é concedido, nunca
     *    autoconquistado (PRD §1.4, princípio 2) — não existe autocadastro. Um
     *    "Criar conta" aqui prometeria uma porta que o banco não tem, e o
     *    voluntário passaria a culpar o sistema por um cadastro que é da
     *    secretaria. Daí `noSelfSignup` ser afirmação, não link.
     * 2. **Não reage ao e-mail digitado.** Nada nesta tela pode mudar conforme
     *    o e-mail exista ou não (regra 6 / ADR-002 / US-02) — nem texto, nem
     *    cor, nem tempo de resposta.
     */
    brand: 'Gestão IBFT',
    church: 'Igreja Batista Filadélfia de Taguatinga',

    /**
     * O painel institucional reproduz a peça de visão da própria igreja
     * (story de 27/05/2025, @ibftonline) — mesma frase, mesmo papel laranja,
     * mesmas três lâminas empilhadas. É o que faz o voluntário reconhecer a
     * marca ANTES de digitar o e-mail; é o que separa "é o painel da IBFT" de
     * "é phishing".
     */
    visionEyebrow: 'Nossa visão',
    visionLead: 'é ser uma igreja família',
    visionLines: ['que acolhe,', 'cresce e', 'transforma vidas'],
    /**
     * A ponte entre a visão da igreja e o que este sistema faz. Sem ela o
     * painel laranja é decoração; com ela, quem entra já sabe para que serve o
     * que vai preencher do outro lado.
     */
    visionBridge:
      'Este painel é onde o que cada ministério planeja fica visível para quem precisa decidir.',

    /* Passo 1 — e-mail. */
    title: 'Entrar no painel',
    subtitle:
      'O acesso é da liderança e vem do cadastro da secretaria. Informe seu e-mail e enviamos um código de 6 dígitos.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'voce@ibft.org.br',
    emailSubmit: 'Receber código de acesso',
    emailSubmitting: 'Enviando…',
    /** Promessa, não instrução: tira do caminho a pergunta "qual era a senha?". */
    /** Travessão não: em duas linhas ele sobra no começo da segunda. */
    noPassword: 'Você não cria nem memoriza senha: cada entrada usa um código novo.',
    noSelfSignup:
      'Ainda não tem acesso? Quem cadastra a liderança é a secretaria da igreja.',

    /* Passo 2 — código. */
    otpTitle: 'Digite o código',
    /**
     * Resposta única para e-mail cadastrado e não cadastrado (ADR-002 / US-02).
     * Não altere para diferenciar os casos: isso criaria um oráculo de
     * enumeração da liderança da igreja.
     */
    otpSent:
      'Se este e-mail estiver cadastrado, você receberá um código de acesso em instantes.',
    otpHint: 'O código tem 6 dígitos e vale por 10 minutos.',
    otpLabel: 'Código de 6 dígitos',
    /**
     * Rótulo do e-mail exibido no passo 2. Diz "informado", e não "enviado
     * para": o que a tela sabe é o que a pessoa digitou — afirmar o envio
     * contradiria o "se este e-mail estiver cadastrado" logo acima.
     */
    otpEmailLabel: 'E-mail informado',
    otpSubmit: 'Entrar',
    otpSubmitting: 'Verificando…',
    otpInvalid: 'Código inválido ou expirado. Peça um novo código de acesso.',
    otpRateLimited:
      'Você pediu vários códigos em pouco tempo. Aguarde alguns minutos antes de tentar de novo.',
    changeEmail: 'Usar outro e-mail',
    resend: 'Reenviar código',
    resending: 'Reenviando…',

    signedOut: 'Você saiu do sistema.',
    noAccess:
      'Sua conta não tem vínculo ativo com nenhum ministério. Fale com a secretaria da igreja.',
  },

  errors: {
    /** Último recurso: nunca mostre a mensagem crua do Postgres. */
    unexpected:
      'Não foi possível concluir a ação. Tente novamente em alguns instantes; se continuar, avise a secretaria.',
    network:
      'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
    notFound:
      'Não encontramos este registro. Ele pode ter sido removido ou você não tem acesso a ele.',
    forbidden: 'Você não tem permissão para esta ação.',
  },

  empty: {
    coordinations: 'Nenhuma coordenação disponível para você.',
    ministries: 'Esta coordenação ainda não tem ministérios cadastrados.',
    projects: 'Nenhum projeto registrado ainda.',
    /** A tela de `/rascunhos` vazia — ver `projects.draftsTitle` para o nome. */
    drafts: 'Nada esperando você.',
    reviewQueue: 'Nenhum projeto aguardando análise. Fila zerada.',
    calendar: 'Nenhum projeto planejado neste período.',
    /**
     * A irmã da frase acima, para o recorte de UM dia (spec-calendar.md §6.1).
     * "Período" descreveria o mês inteiro e faria a lista parecer contradizer a
     * grade logo acima, que mostra os outros dias cheios.
     */
    calendarDay: 'Nada marcado neste dia.',
    /** A Início de quem ainda não tem nada no alcance — ver `home.emptyHint`. */
    home: 'Ainda não há projetos no seu alcance.',
  },

  /**
   * A aba Início — painel de insights e atalhos.
   *
   * ⚠️ **Toda frase daqui carrega DE QUEM é o número.** Não é preciosismo de
   * redação: cada contagem desta tela chega recortada pela RLS, e uma legenda
   * sem moldura afirma sobre a igreja o que só vale para quem está olhando. Foi
   * esse defeito que tirou o panorama consolidado da tela de coordenação (PRD
   * US-11), e a Início só pode carregá-lo porque a moldura dela é "você".
   *
   * A exceção é `densityTitle`, e ela é explícita: aquele bloco fala da FILIAL
   * inteira, para os cinco papéis ([ADR-015](../../../docs/adr/ADR-015-agenda-volatil.md)).
   * Dois escopos na mesma tela são legítimos; dois escopos sem rótulo, não.
   */
  home: {
    /** A moldura dos blocos recortados pela RLS. Vai colada ao número. */
    scopeReach: 'no seu alcance',
    emptyHint:
      'Quando o seu ministério registrar um projeto, ele aparece aqui — e nos gráficos desta tela.',

    statusTitle: 'Como está o seu escopo',
    statusCaption: 'Os projetos enviados ao seu alcance, pela situação em que estão.',
    volumeTitle: 'Volume por mês',
    volumeCaption: 'Quando os projetos acontecem, no seu alcance.',
    /** ⚠️ "na igreja", e não "no seu alcance" — ver o aviso acima. */
    densityTitle: 'Próximas semanas na igreja',
    densityCaption: 'Tudo que está marcado na filial, para você não planejar contra um dia cheio.',
    upNextTitle: 'O que vem por aí',
    upNextCaption: 'Os próximos do seu alcance, por data de execução.',
    pipelineTitle: 'Saúde da esteira',
    pipelineCaption: 'Os projetos que passaram por decisão nos últimos 90 dias.',
    activityTitle: 'Onde está a atividade',
    activityCaption: 'Projetos enviados nos últimos 6 meses, por ministério.',

    /**
     * Os atalhos. Cada número abre a tela que o produz — nunca uma soma.
     *
     * ⚠️ Não há atalho para a fila de análise: ela já é item da sidebar, com
     * contador próprio, e o bloco "Saúde da esteira" já diz quantos estão
     * parados. Um terceiro aviso da mesma pendência, na mesma tela, é ruído.
     */
    draftsShortcut: (count: number) => (count === 1 ? '1 rascunho seu' : `${count} rascunhos seus`),
    adjustmentsShortcut: (count: number) =>
      count === 1 ? '1 projeto esperando seu ajuste' : `${count} projetos esperando seu ajuste`,

    medianLabel: 'Tempo até decisão',
    medianHint: 'Mediana. A meta da igreja é decidir em até 7 dias.',
    /** Sem amostra o número não existe — e dizer "0 dias" seria inventá-lo. */
    medianEmpty: 'Nenhuma decisão tomada nos últimos 90 dias.',
    stalledLabel: 'Parados na fila',
    stalledHint: (threshold: number) => `Aguardando decisão há mais de ${threshold} dias.`,
    decidedLabel: 'Decisões em 90 dias',

    /**
     * ⚠️ **"enviado", e nunca "sem atividade".** A RLS esconde rascunho de
     * ministério onde quem olha não tem vínculo direto, então um ministério que
     * só tem rascunho chega aqui zerado. "Sem atividade" seria uma afirmação
     * falsa feita com dado que a pessoa não tem; "não enviou projeto" continua
     * verdadeiro haja rascunho ou não.
     */
    silentTitle: (count: number) =>
      count === 1
        ? '1 ministério não enviou projeto nos últimos 6 meses'
        : `${count} ministérios não enviaram projeto nos últimos 6 meses`,
  },

  /**
   * O calendário (spec-calendar-ux.md).
   *
   * ⚠️ `empty.calendarNoSources` saiu daqui junto com o seletor de fontes
   * (decisão 89): não existe mais o estado "nenhum calendário ligado", porque
   * não existe lista de calendários — existe a agenda da igreja.
   */
  calendar: {
    /** Os dois cabeçalhos da caixa de entrada (§5.1). */
    inboxPending: 'Minhas Pendências',
    inboxUpdates: 'Atualizações',
    /** O verbo da pendência: a pessoa volta ao formulário para ajustar o que foi pedido. */
    resolve: 'Ajustar',
    /**
     * A linha única de quem decide — ela LEVA à fila, não a reimplementa.
     * Singular e plural porque "1 projetos" é o tipo de descuido que faz o
     * voluntário desconfiar do resto do número.
     */
    reviewQueueLine: (count: number) =>
      count === 1 ? '1 aguardando sua decisão' : `${count} aguardando sua decisão`,
    /**
     * A folha de filtros — agora em qualquer largura, ao lado do mês (§18).
     * "Precisa de você" continua sendo folha só abaixo de 1024px (§2.2).
     */
    filterSheetTitle: 'Filtrar',
    filterSheetDescription: 'Marque o que quer ver. O recorte vale para tudo que está na grade.',
    filterSheetClear: 'Limpar filtros',
    filterSheetDone: 'Concluir',
    /** Mês sem nada marcado: não há o que recortar, e dizer isso evita a busca. */
    filterSheetEmpty: 'Ainda não há nada neste período para filtrar.',
    inboxSheetTitle: 'Precisa de você',
  },

  /**
   * Rótulos de `support_group` (ADR-010). O enum é inglês no banco — quem
   * escolhe a palavra em português é sempre esta camada (RNF-U04).
   */
  supportGroups: {
    media: 'Comunicação e mídia',
    technical: 'Técnica',
    arts: 'Ministrações e artes',
    operations: 'Operação e alinhamento',
  },

  draft: {
    saving: 'Salvando…',
    saved: 'Rascunho salvo',
    /**
     * Estado de repouso do indicador, ao lado do título do projeto.
     *
     * É promessa, não relatório: fica visível antes de existir qualquer save,
     * porque o medo de perder o que se escreveu aparece no primeiro parágrafo
     * — não no primeiro salvamento.
     */
    autosaveHint: 'Rascunho salvo automaticamente',
    /**
     * As mesmas duas frases no modo ajuste (decisão 48). Quem está corrigindo
     * um projeto devolvido **não tem um rascunho, tem um projeto** — a promessa
     * é idêntica, só o substantivo muda. Dizer "rascunho" ali sugeriria que o
     * que ele já enviou virou outra coisa.
     */
    savedAdjustments: 'Alterações salvas',
    autosaveHintAdjustments: 'Alterações salvas automaticamente',
    /** ADR-005 — concorrência otimista por `version`. */
    conflict:
      'Este rascunho foi alterado em outro dispositivo. Recarregue para ver a versão mais recente.',
  },

  /**
   * Os títulos das seções do documento de projeto — **os mesmos de quem
   * escreve e de quem lê** (spec-project-review-ux.md decisão 18).
   *
   * Eram literais dos dois lados, e era questão de tempo até um virar "Data e
   * local" e a leitura de quem decide deixar de casar com a escrita de quem
   * preencheu. O formulário consome daqui por `FORM_SECTIONS`; a tela de
   * detalhe, direto.
   *
   * `extraInfo` e `timeline` não são seções do formulário: a primeira é um
   * CAMPO dentro de "Responsável e revisão" que vira seção própria no
   * documento lido, e a segunda só existe depois que há história.
   */
  projectSections: {
    identity: 'Identificação',
    objective: 'Objetivo',
    /**
     * "Data e Local" desde a 3ª rodada (decisão 37) — e a troca pegou as TRÊS
     * telas de uma vez, que é exatamente para isso que esta constante foi
     * extraída. Renomear metade delas é o começo da divergência que ela existe
     * para impedir.
     */
    schedule: 'Data e Local',
    support: 'Apoio e custos',
    review: 'Responsável e revisão',
    extraInfo: 'Informações complementares',
    timeline: 'Linha do tempo',
  },

  /**
   * Formulário de projeto (spec-project-form.md §7.1).
   *
   * As cinco seções vivem numa tela só, roladas de cima a baixo — não há
   * "etapa 3 de 5" para escrever aqui. O que substitui isso é o texto de
   * progresso (`progress`), que fala de QUANTO do documento está pronto.
   */
  form: {
    newProject: 'Novo projeto',
    /* A promessa do autosave saiu daqui: ela agora mora ao lado do título, em
       `draft.autosaveHint`. Repetir a mesma garantia em dois lugares da mesma
       tela não tranquiliza o dobro — só faz a segunda parecer outra coisa. */
    newProjectHint:
      'Preencha as seções abaixo. Dá para sair e continuar depois, em qualquer aparelho.',
    optional: 'Opcional',
    /** Índice lateral: quantas das seções obrigatórias já estão completas. */
    progress: (done: number, total: number) => `${done} de ${total} seções obrigatórias`,
    sectionComplete: 'Seção completa',
    sectionMissing: 'Falta preencher',
    sectionEmpty: 'Ainda não preenchida',
    onBehalfOf: 'Em nome de',
    /** US-08: com um vínculo só, o select vira texto — mas a indicação fica. */
    singleMinistryHint: 'Você lidera um ministério, e o projeto será criado nele.',
    missingBeforeSubmit: 'Para enviar, ainda falta:',
    submitting: 'Enviando…',

    /**
     * Revisão antes do envio (a folha).
     *
     * O botão do documento NÃO envia — ele abre a revisão. A distinção é a
     * razão de a folha existir: enviar é a única ação irreversível que um
     * líder toma neste painel (o projeto sai das mãos dele), e ela estava a um
     * clique de distância de quem só queria conferir se o resumo batia.
     */
    reviewAndSubmit: 'Revisar e enviar',
    reviewTitle: 'Confira antes de enviar',
    reviewDescription:
      'É isto que a liderança vai ler. Depois de enviado, o projeto sai da sua edição até haver uma decisão.',
    reviewBack: 'Voltar e ajustar',
    sentTitle: 'Projeto enviado',
    sentHint: 'Ele entrou na fila de análise. Você acompanha a situação na tela do projeto.',

    /**
     * A moldura do MODO AJUSTE (decisão 48) — a volta do líder com um projeto
     * devolvido (spec-project-adjustments.md §4.1).
     *
     * A tela é a mesma; o que muda é toda frase que fala de "novo" ou de
     * "rascunho", porque as três mentem num reenvio: o projeto não é novo, o
     * que ele mexe não é rascunho, e o que ele faz no fim não é enviar pela
     * primeira vez. A distinção do verbo importa para quem decide também — o
     * revisor precisa saber que está lendo uma 2ª rodada.
     */
    adjustHint:
      'Faça os ajustes pedidos e reenvie para análise. O que você mudar fica salvo, mesmo que você envie depois.',
    reviewAndResubmit: 'Revisar e reenviar',
    resubmitTitle: 'Confira antes de reenviar',
    resubmitDescription:
      'É isto que a liderança vai ler nesta rodada. Depois de reenviado, o projeto volta para a fila de análise e sai da sua edição.',
    resubmit: 'Reenviar para análise',
    resubmitting: 'Reenviando…',
    resentTitle: 'Projeto reenviado',
    resentHint: 'Ele voltou para a fila de análise. Você acompanha a situação na tela do projeto.',

    /**
     * O pedido do revisor dentro do formulário (decisões 45 e 46).
     *
     * **Não é dispensável e não é truncado**: é a instrução vigente da tarefa
     * que a pessoa veio fazer. "Marcar como lido" exigiria estado por usuário —
     * tabela nova para resolver nada.
     */
    /**
     * A observação de reenvio (decisão 47) — a decisão 16 da spec do formulário
     * finalmente exposta: a aresta `changes_requested → under_review` já é
     * `comment_required = false` e `submitProject` já mandava o texto; só a tela
     * não perguntava.
     *
     * **Não reabre comentário livre** (PRD §5.2): o texto está preso a uma
     * transição, que é a definição exata do que o sistema permite. E ele é o
     * PORQUÊ em linguagem humana — o *quê* já vem de graça, campo a campo, pelo
     * `projects_field_change_writer` (§5.1).
     */
    resubmitCommentLabel: 'O que você mudou?',
    resubmitCommentHint:
      'Opcional. Ajuda quem vai analisar a ver o que mudou desde a devolução.',

    reviewerRequestTitle: 'O que pediram para ajustar',
    reviewerRequestBy: (name: string, date: string) => `${name} · ${date}`,
    reviewerRequestCollapse: 'Recolher o pedido',
    reviewerRequestExpand: 'Ver o pedido de novo',

    discard: 'Descartar rascunho',
    discardConfirmTitle: 'Descartar este rascunho?',
    discardConfirm:
      'Ele sai da sua lista de rascunhos e não há como recuperá-lo por aqui. O que você escreveu se perde.',
    discardConfirmAction: 'Sim, descartar',
    discardKeep: 'Manter rascunho',
    discarding: 'Descartando…',
    supportHint: 'Marque o que você vai precisar. Detalhar ajuda quem atende, mas não é obrigatório.',
    supportRequestLabel: (typeName: string) => `O que você precisa de ${typeName}?`,
    supportNeededBy: 'Precisa até',
    /**
     * As três frases da regra de data (`features/projects/lib/schedule.ts`).
     *
     * Elas nomeiam a METADE errada — data ou horário —, e não o par: quem
     * marcou 11/10 às 20h e 11/10 às 18h escreveu a data certa, e "o término
     * precisa ser depois do início" manda essa pessoa reler os quatro campos
     * para achar o que já se sabe qual é.
     */
    startInPast: 'A data de início precisa ser a partir de amanhã.',
    endDateBeforeStart: 'A data de término não pode ser anterior à de início.',
    endTimeBeforeStart: 'O horário de término precisa ser depois do de início.',
    conflictTitle: 'Este espaço já tem coisa marcada nesse horário',
    /**
     * US-20. ⚠️ **Mudou com a decisão 70** (spec-calendar.md §7.3): a frase
     * dizia "outro projeto", sem nome, e agora a lista logo abaixo nomeia o
     * projeto e o ministério. O texto continua carregando a parte que importa —
     * *você pode seguir assim mesmo* —, porque o alerta é não bloqueante e a
     * decisão final é humana (regra 5 / ADR-004).
     */
    conflictHint:
      'Um projeto já confirmado ocupa este espaço no mesmo horário. Você pode seguir assim mesmo — quem analisa decide.',
    leadTimeTitle: 'Está em cima da hora',
    budgetHint: 'Valor estimado, só para planejar. O sistema não é o financeiro da igreja.',
  },

  /** Textos da tela de um projeto (fora do formulário e da análise). */
  projects: {
    /**
     * A leitura reduzida (spec-calendar.md §7.2, decisão 76).
     *
     * Diz o RECORTE e para onde ir, não o motivo técnico (RNF-U03): "a RLS não
     * devolveu a linha" é verdade e não ajuda ninguém. "O planejamento completo
     * fica com o ministério responsável" diz a quem perguntar.
     */
    agendaOnly:
      'Você está vendo o que a agenda da igreja mostra deste projeto. O planejamento completo fica com o ministério responsável.',
    /**
     * Decisão 30: contato escondido quando o vínculo acabou — e a frase diz o
     * que fazer, não só o que houve (RNF-U03).
     */
    contactInactive: 'Sem vínculo ativo. Fale com a liderança do ministério.',

    /**
     * Os dois fatos da seção "Data e Local" (decisão 37). Eram "Quando" e
     * "Onde": advérbio de pergunta é o vocabulário do FORMULÁRIO, onde ainda se
     * está perguntando. No documento a resposta já existe, e o rótulo deve
     * nomear o dado que está ali embaixo.
     */
    factDateTime: 'Data e Hora',
    factPlace: 'Local',

    /**
     * A porta do modo ajuste (decisões 42 e 43) — a única ação primária do
     * cabeçalho para quem é do ministério. "Cancelar projeto" continua lá
     * embaixo, na seção de saída, justamente para não disputar com ela.
     *
     * **Dois rótulos, um botão**: o verbo diz em que ponto da rodada a pessoa
     * está, e a escolha entre eles é `hasUnsentAdjustments` — dado do servidor,
     * não estado de sessão, então ele sobrevive ao F5.
     */
    adjust: 'Ajustar projeto',
    adjustContinue: 'Continuar ajustes',

    /**
     * A tela de `/rascunhos`, alcançada pelo botão "Rascunhos" na barra do
     * Calendário e no cabeçalho de `/projetos`.
     *
     * ⚠️ **Ela se chamou "Minhas pendências" (decisão 49), e o nome caiu junto
     * com o item de menu.** O caso de uso que aquele nome cobria — *"o que está
     * parado esperando você"* — passou a ser respondido pela caixa de entrada do
     * Calendário, que mostra o devolvido com o verbo ao lado. Sobrou o rascunho,
     * que a caixa não mostra porque ele não é intenção ainda (regra 8) — e
     * "Rascunhos" é o nome que a pessoa usa para ele.
     *
     * A lista **continua trazendo o devolvido também**, com crachá: é o mesmo
     * "parado comigo", e tirá-lo daqui deixaria a caixa de entrada como caminho
     * único até ele. A descrição diz isso em voz alta, para o nome não prometer
     * menos do que a tela entrega.
     *
     * A URL continua `/rascunhos`: rota em português já publicada, e renomear
     * quebraria link salvo sem ganho.
     */
    draftsTitle: 'Rascunhos',
    draftsDescription:
      'O que você começou e ainda não enviou — e o que voltou para ajuste.',
    draftsEmptyHint:
      'Projeto que você salvar sem enviar aparece aqui, junto com o que voltar para ajuste.',
    /**
     * Rascunho continua sendo "continuar"; devolvido usa o verbo da porta —
     * curto aqui porque o crachá ao lado já disse o que é ("Ajustes
     * solicitados"), e repetir "projeto" no botão de uma lista de projetos não
     * acrescenta nada.
     */
    pendingContinueDraft: 'Continuar',
    pendingAdjust: 'Ajustar',
  },

  review: {
    commentRequiredOnReject: 'Explique o motivo da rejeição para o ministério.',
    commentRequiredOnChanges: 'Descreva o que precisa ser ajustado.',
    /** US-20 — alerta não bloqueante: informa, não impede. Do lado do REVISOR
     *  (detalhe completo); a versão do líder é `form.conflictTitle/Hint`. */
    spaceConflictTitle: 'Atenção: possível conflito de espaço',
    spaceConflictHint:
      'Existe outro projeto confirmado neste mesmo espaço com horário sobreposto. Você pode aprovar mesmo assim.',

    /** Os três verbos da análise (spec-project-review.md decisão 3). */
    actions: {
      approve: 'Aprovar',
      requestChanges: 'Solicitar ajustes',
      reject: 'Rejeitar',
    },
    decisionTitle: 'Decisão',
    /*
       ⛔ **`startAnalysis` foi removida na decisão 107, e não deve voltar.** Ela
       rotulava o botão que escondia os três verbos — `useState` puro, sem RPC.
       Cobrava um clique em toda análise real sem proteger estado nenhum, e o
       nome tinha o defeito de parecer transição: "Iniciar análise" era o rótulo
       de `start_review` na 1ª rodada, RPC que a decisão 25 removeu e a ADR-013
       apagou do banco. Se um rótulo assim reaparecer aqui, é sinal de que o
       portão está voltando junto. */
    deciding: 'Enviando decisão…',

    /**
     * Sair no meio de uma análise (decisão 40) — **só quando há texto escrito**.
     * Abrir um verbo e sair sem escrever nada não pergunta nada: obstáculo sem
     * objeto atrás treina o dedo a confirmar sem ler, inclusive no dia em que
     * houver objeto.
     *
     * ⚠️ **A copy não menciona fila nem devolução**, e a ausência é a decisão.
     * O projeto nunca saiu da fila — abrir a tela e escolher um verbo não é
     * transição nenhuma. Uma frase prometendo devolução ensinaria o revisor a
     * acreditar num mecanismo inexistente. O que de fato se perde é a
     * observação digitada.
     */
    exitGuardTitle: 'Sair sem registrar a decisão?',
    exitGuardDescription: 'A observação que você escreveu será perdida.',
    exitGuardStay: 'Continuar analisando',
    exitGuardLeave: 'Sair',

    /** Depois da decisão (decisão 17) — decidir não pode terminar no vazio. */
    decisionRecorded: 'Decisão registrada',
    backToQueue: (remaining: number) =>
      remaining === 1 ? 'Voltar para a fila (1 restante)' : `Voltar para a fila (${remaining} restantes)`,
    backToQueueEmpty: 'Voltar para a fila',
    /** §5.4: perder a corrida não pede nada do usuário — a tela já se corrigiu. */
    decidedFirstBy: (name: string) => `${name} decidiu este projeto primeiro. A tela foi atualizada.`,
    decidedFirstByUnknown: 'Outra pessoa decidiu este projeto primeiro. A tela foi atualizada.',

    /** A fila `/analise` (US-18, §6.3). */
    queueTitle: 'Análise',
    queueDescription: 'Projetos aguardando sua decisão.',
    /**
     * Fila zerada (decisão 31) — **três** estados vazios, e confundi-los é o
     * erro. O elogio é do primeiro e só dele: ele exige que tenha havido
     * trabalho nos últimos 30 dias. Fila vazia por instalação nova recebe o
     * segundo, informativo. O terceiro é de quem não decide nada aqui.
     */
    queueAllDoneTitle: 'Tudo em dia.',
    queueAllDoneDescription: (count: number, truncated: boolean) =>
      `Nenhum projeto esperando decisão agora. Nos últimos 30 dias, a equipe decidiu ${
        truncated ? `mais de ${count}` : count
      } ${count === 1 && !truncated ? 'projeto' : 'projetos'}.`,
    queueNeverDecidedTitle: 'Nada esperando decisão.',
    queueNeverDecidedDescription: 'Quando um ministério enviar um projeto, ele aparece aqui.',
    queueEmptyNotReviewer: 'Esta tela é da equipe que analisa projetos.',
    /**
     * O verbo da última coluna da fila — **a única forma de abrir o projeto
     * dali**. A linha inteira deixou de navegar: numa tela cujo propósito é
     * decidir, "abri sem querer" e "abri para decidir" não podem ser o mesmo
     * gesto.
     *
     * A versão longa é para o leitor de tela: vinte links dizendo "Analisar"
     * fora do contexto da linha não distinguem um projeto do outro.
     */
    queueRowAction: 'Analisar',
    queueRowActionFor: (title: string | null) =>
      title ? `Analisar ${title}` : 'Analisar projeto sem título',
    round: (n: number) => `${n}ª rodada`,
    firstSubmissionOn: (date: string) => `1ª submissão em ${date}`,

    /**
     * A linha de atribuição do cabeçalho (decisão 14) — um template por
     * situação. A ESCOLHA de qual usar, e o cálculo de dias de espera, são
     * lógica — moram em `features/projects/lib/attribution.ts`, com o mesmo
     * `switch` exaustivo sem `default` de `timelineFor`.
     *
     * **É aqui que "Enviado" sobrevive**, e é o lugar certo dele: o crachá diz
     * ONDE o projeto está, esta linha diz O QUE aconteceu (ADR-013).
     */
    /**
     * ⚠️ **Fica com este nome, e não vira `attributionUnderReview`** (ADR-013,
     * decisão 2): ela descreve o ATO, e o ato continua se chamando enviar. É o
     * estado que passou a se chamar "em análise".
     */
    attributionSubmitted: (name: string, date: string) => `Enviado por ${name} em ${date}`,
    attributionChangesRequested: (name: string, date: string) =>
      `Ajustes solicitados por ${name} em ${date}`,
    attributionApproved: (name: string, date: string) => `Aprovado por ${name} em ${date}`,
    attributionRejected: (name: string, date: string) => `Rejeitado por ${name} em ${date}`,
    attributionCancelled: (name: string, date: string) => `Cancelado por ${name} em ${date}`,
    attributionCompleted: (name: string, date: string) => `Concluído por ${name} em ${date}`,
    /** decisão 1b / spec §6.4: a auto-aprovação fica visível, não proibida. */
    attributionSelfMarker: 'autor do projeto',
    attributionWaitingSuffix: (daysLabel: string) => ` · ${daysLabel}`,

    /**
     * "Decidido nos últimos 30 dias" (decisão 28). A escolha de qual frase usar
     * é lógica, e mora em `features/review/lib/historyLine.ts`.
     */
    historyTitle: 'Decidido nos últimos 30 dias',
    historyEmpty: 'Nenhuma decisão nos últimos 30 dias.',
    historyTruncated: 'Mostrando as 50 decisões mais recentes.',
    historyByYou: 'você',
    historyApproved: (who: string) => `aprovado por ${who}`,
    historyChangesRequested: (who: string) => `ajustes solicitados por ${who}`,
    historyRejected: (who: string) => `rejeitado por ${who}`,
    historyCancelled: (who: string) => `cancelado por ${who}`,
    /** Só para o ramo que a consulta não produz — ver o `switch` do historyLine. */
    historyDecided: (who: string) => `decidido por ${who}`,

    /**
     * Vizinhança da data (§6.5) — contexto, não lista: nunca navega.
     *
     * ⚠️ **O título dizia "Nesta semana" e a consulta cobria 28 dias**
     * (decisão 38). Quem lia acreditava no título e concluía que a igreja tinha
     * menos coisa marcada do que tem. O intervalo real vai embaixo, formatado a
     * partir da própria janela consultada — e não escrito à mão, que sairia de
     * sincronia no dia em que a janela mudar.
     */
    neighborhoodTitle: 'Perto desta data',
    neighborhoodRange: (from: string, to: string) => `${from} – ${to}`,
    neighborhoodEmpty: 'Nada mais neste período.',
    neighborhoodCurrentMarker: 'este projeto',

    /**
     * Leitura DERIVADA do relógio, nunca um status (decisão 29): `approved`
     * com a janela do evento já encerrada. Ninguém declara que um evento
     * aconteceu — a data declara.
     */
    awaitingReport: 'Aguardando relatório',

    cancelProject: 'Cancelar projeto',
    cancelSectionHint:
      'Use quando o evento não vai mais acontecer. O projeto sai do calendário e o motivo fica no histórico.',
    cancelConfirmTitle: 'Cancelar este projeto?',
    cancelConfirmDescription:
      'O evento não vai acontecer. Descreva o motivo — quem acompanha o ministério vai ler isto no histórico.',
    cancelling: 'Cancelando…',
  },

  actions: {
    retry: 'Tentar novamente',
    back: 'Voltar',
    save: 'Salvar',
    cancel: 'Cancelar',
    submit: 'Enviar para análise',
  },
} as const;
